import { useState } from 'react';
import SelectedCourtCard from './SelectedCourtCard';
import { useMediaQuery } from '../hooks/useMediaQuery';
import { groupSelectedRowsByCourt } from '../utils/selectedSlotsUtil';
import { TIME_SLOT_SUMMARY_STACKED_QUERY } from '../constants/bookingConst';
import '../css/SelectedSlotsSummary.css';

/**
 * The guest's selected hours on the time slot screen, as one card per court
 * (per date, once hours are picked on more than one date).
 *
 * Beside the grid (large screens) every card is open. On small screens
 * (TIME_SLOT_SUMMARY_STACKED_QUERY), where the summary floats over the bottom
 * of the screen, each card starts folded to its header — court name and hour
 * count — and opens on a tap, so the floating panel stays short.
 *
 * @param {object} props
 * @param {Array<{dateStr: string, rows: Array<object>}>} props.groups - selected hours bucketed by date, in date order
 * @param {boolean} props.showDates - label each date (2+ dates selected)
 * @param {(dateStr: string) => string} props.formatDate - date heading text
 * @param {(row: {dateStr: string, timeSlot: string, court: number}) => void} props.onRemove - deselects one hour
 * @returns {JSX.Element}
 */
function SelectedSlotsSummary({ groups, showDates, formatDate, onRemove }) {
  const stacked = useMediaQuery(TIME_SLOT_SUMMARY_STACKED_QUERY);
  // Cards the guest has opened, keyed "date|court". Folded is the default on
  // small screens, so a newly picked court only adds a header line.
  const [expanded, setExpanded] = useState(() => new Set());

  /**
   * Folds or unfolds one court card.
   * @param {string} key - "date|court"
   * @returns {void}
   */
  const toggle = (key) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  return (
    <div className="sss">
      {groups.map(group => (
        <div className="sss-date-group" key={group.dateStr}>
          {showDates && <div className="sss-date">{formatDate(group.dateStr)}</div>}
          <div className="sss-cards">
            {groupSelectedRowsByCourt(group.rows).map(({ court, rows }) => {
              const key = `${group.dateStr}|${court}`;
              return (
                <SelectedCourtCard
                  key={key}
                  court={court}
                  rows={rows}
                  onRemove={onRemove}
                  collapsible={stacked}
                  expanded={expanded.has(key)}
                  onToggle={() => toggle(key)}
                />
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}

export default SelectedSlotsSummary;
